import React from 'react';
import { CheckCircle, XCircle, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import TicketCard from '../TicketCard';

export const TicketManagementPanel = ({
  pendingTickets = [],
  completedTickets = [],
  assignedTickets = [],
  closedTickets = [],
  technicians = [],
  onAssignTechnician,
  onCloseTicket,
  onShowRejectDialog
}) => {
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-zinc-900">Bütün Ticketlər</h2>
        <div className="flex items-center gap-4 text-sm">
          <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full">Gözləyən: {pendingTickets.length}</span>
          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Təyin olunmuş: {assignedTickets.length}</span>
          <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full">Tamamlanmış: {completedTickets.length}</span>
          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full">Bağlanmış: {closedTickets.length}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Pending Tickets */}
        <section>
          <h3 className="text-lg font-bold mb-4 text-zinc-900 flex items-center gap-2">
            <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
            Gözləyən Ticketlər ({pendingTickets.length})
          </h3>
          {pendingTickets.length === 0 ? (
            <p className="text-zinc-500">Gözləyən ticket yoxdur</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {pendingTickets.map(ticket => (
                <TicketCard key={ticket.id} ticket={ticket}>
                  {ticket.previous_technician_id && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700 font-semibold">
                        ⚠️ Bu ticket geri qaytarılmışdır! ({ticket.previous_technician_full_name || ticket.previous_technician_name})
                      </p>
                      {ticket.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">Səbəb: {ticket.rejection_reason}</p>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Select onValueChange={(value) => onAssignTechnician(ticket.id, value)}>
                      <SelectTrigger className="flex-1 bg-primary text-white border-primary" data-testid={`admin-assign-tech-${ticket.id}`}>
                        <SelectValue placeholder="Texnik seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {technicians.map(tech => {
                          const isDisabled = ticket.previous_technician_id === tech.id;
                          return (
                            <SelectItem 
                              key={tech.id} 
                              value={tech.id}
                              disabled={isDisabled}
                              className={isDisabled ? 'text-red-500 line-through bg-red-50' : 'text-zinc-900'}
                            >
                              {tech.full_name || tech.username}
                              {isDisabled && ' (geri qaytarılıb)'}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </TicketCard>
              ))}
            </div>
          )}
        </section>

        {/* Completed Tickets */}
        <section>
          <h3 className="text-lg font-bold mb-4 text-zinc-900 flex items-center gap-2">
            <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
            Tamamlanmış Ticketlər ({completedTickets.length})
          </h3>
          {completedTickets.length === 0 ? (
            <p className="text-zinc-500">Tamamlanmış ticket yoxdur</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {completedTickets.map(ticket => (
                <TicketCard key={ticket.id} ticket={ticket}>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => onCloseTicket(ticket.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`admin-close-ticket-${ticket.id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Bağla
                    </Button>
                    <Button
                      onClick={() => onShowRejectDialog(ticket)}
                      variant="destructive"
                      className="flex-1"
                      data-testid={`admin-reject-ticket-${ticket.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Geri qaytart
                    </Button>
                  </div>
                  <a
                    href={`tel:${ticket.subscriber_phone}`}
                    className="flex items-center justify-center gap-2 mt-2 p-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {ticket.subscriber_phone}
                  </a>
                </TicketCard>
              ))}
            </div>
          )}
        </section>

        {/* Assigned Tickets */}
        <section>
          <h3 className="text-lg font-bold mb-4 text-zinc-900 flex items-center gap-2">
            <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
            Təyin olunmuş Ticketlər ({assignedTickets.length})
          </h3>
          {assignedTickets.length === 0 ? (
            <p className="text-zinc-500">Təyin olunmuş ticket yoxdur</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {assignedTickets.map(ticket => (
                <TicketCard key={ticket.id} ticket={ticket} />
              ))}
            </div>
          )}
        </section>

        {/* Closed Tickets */}
        <section>
          <h3 className="text-lg font-bold mb-4 text-zinc-900 flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-full"></span>
            Bağlanmış Ticketlər ({closedTickets.length})
          </h3>
          {closedTickets.length === 0 ? (
            <p className="text-zinc-500">Bağlanmış ticket yoxdur</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {closedTickets.slice(0, 20).map(ticket => (
                <TicketCard key={ticket.id} ticket={ticket} />
              ))}
              {closedTickets.length > 20 && (
                <p className="text-center text-zinc-500 text-sm py-2">
                  ... və daha {closedTickets.length - 20} ticket
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default TicketManagementPanel;
